import { useLoadingAnimation } from '@/components/LoadingAnimation/useLoadingAnimation.hook';
import { DEFAULT_ERROR_MESSAGE } from '@/config/text.const';
import { HttpStatusCode } from '@/services/http/HttpStatusCodes';
import { varBillingFunctionsClient } from '@/services/http/clients/varBillingFunctionsClient';
import { notificationService } from '@/services/notification/notification.client';

export type VarBillingUploadFields = {
  file: File | undefined;
  columnMappings?: Map<string, string>;
  numberOfRows?: number;
};

type FileUploadProps = {
  values: VarBillingUploadFields;
  successCallback?: (data: { uploadId: string; fileName: string; numberOfRows: number }) => void;
  errorCallback?: () => void;
};

export default function useVarBillingUploadFile() {
  const { toggleLoadingAnimation } = useLoadingAnimation();

  const onFileUpload = async ({ values, successCallback, errorCallback }: FileUploadProps) => {
    toggleLoadingAnimation(true);
    const uploadedFile = new FormData();

    if (values.file) {
      uploadedFile.append('file', values.file);
    }
    
    if (values.columnMappings) {
      const mappingsObj = Object.fromEntries(values.columnMappings);
      uploadedFile.append('columnMappings', JSON.stringify(mappingsObj));
    }

    if (values.numberOfRows !== undefined) {
      uploadedFile.append('numberOfRows', values.numberOfRows.toString());
    }

    return varBillingFunctionsClient.post('/api/varbilling/upload', uploadedFile, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    })
      .then((uploadResult) => {
        if (uploadResult?.status === HttpStatusCode.OK) {
          const data = uploadResult.data as { uploadId: string; fileName: string; numberOfRows: number };
          successCallback?.(data);
          notificationService.open({
            title: 'Success',
            text: 'File uploaded successfully',
            type: 'success'
          });
        }
      })
      .catch((err) => {
        notificationService.open({
          title: 'Upload file',
          text: err?.message ?? DEFAULT_ERROR_MESSAGE,
          type: 'danger'
        });
        errorCallback?.();
      })
      .finally(() => toggleLoadingAnimation(false));
  };

  return {
    onFileUpload
  };
}
